import Box, { BoxProps } from '@mui/material/Box';

/**
 * Fill-height: let one element on a page stretch to the bottom of the viewport
 * without any of its ancestors knowing about it.
 *
 * Usage — wrap the thing that should grow (typically a table):
 *
 *   <FillHeight>
 *     <DataGrid ... />
 *   </FillHeight>
 *
 * That is the whole per-page change. `fillHeightGlobalStyles` (registered once
 * in each app's MuiCssBaseline overrides) uses `:has()` so that every element
 * between the dashboard <main> and the <FillHeight> becomes a growing flex
 * column, handing the free viewport height down the tree. Removing the
 * <FillHeight> removes every effect.
 *
 * Behaviour:
 * - On a tall viewport the slot absorbs all free space below its siblings.
 * - On a short viewport it floors at FILL_HEIGHT_MIN and the page scrolls
 *   normally.
 * - The child gets a definite height (grid cell). DataGrid and Material React
 *   Table need no extra options; other children should fill their parent
 *   (e.g. height: 100%).
 *
 * Limitation: an ancestor laid out as a row (row <Stack>, <Grid container>)
 * is forced to a column by the rule. Side-by-side layouts need hand-built flex
 * and should not use this component.
 */

export const FILL_HEIGHT_CLASS = 'ac-fill-height';

export const FILL_HEIGHT_MIN = 300;

export const fillHeightGlobalStyles = {
  // Every ancestor on the path from <main> down to the fill target passes the
  // free height through. Siblings of that path are untouched.
  [`#main-content :has(.${FILL_HEIGHT_CLASS})`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  [`.${FILL_HEIGHT_CLASS}`]: {
    // Basis 0 keeps the child's content out of the page's intrinsic height, so
    // the slot sizes purely from free space, floored at the min height.
    flex: '1 1 0',
    minHeight: FILL_HEIGHT_MIN,
    // A single grid track clamped to the slot gives the child a definite
    // height that its own content cannot inflate.
    display: 'grid',
    gridTemplateRows: 'minmax(0, 1fr)',
  },
  // Material React Table support: MRT renders <Paper><toolbar/><TableContainer/><toolbar/></Paper>
  // with no height handling of its own. Make the Paper a column that fills the slot, let the
  // container take the free height and scroll internally, and pin the header while it scrolls.
  // Nothing per-table is needed; DataGrid ignores these rules (its root is not a Paper).
  [`.${FILL_HEIGHT_CLASS} > .MuiPaper-root`]: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  [`.${FILL_HEIGHT_CLASS} > .MuiPaper-root > .MuiTableContainer-root`]: {
    flex: '1 1 0',
    minHeight: 0,
    overflow: 'auto',
  },
  [`.${FILL_HEIGHT_CLASS} > .MuiPaper-root .MuiTableHead-root`]: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
} as const;

export function FillHeight({ className, ...props }: BoxProps) {
  return (
    <Box
      className={className ? `${FILL_HEIGHT_CLASS} ${className}` : FILL_HEIGHT_CLASS}
      {...props}
    />
  );
}
